/**
 * Entity projection — the storage-side view of a split-canonical entity
 * for each Durable Object authority.
 *
 * For an entity declared `authority: 'both', mirror: 'split'` (planned
 * for `cards` and `deckProperties` in Phase C slice 2/3), each row has
 * a different shape depending on where it's stored:
 *
 *   - **UserDo** holds the union: identity + shared + personal. Personal
 *     fields are canonical here; shared fields are a snapshot the
 *     StudyGroupDo broadcasts back to keep linked-deck reads fast.
 *
 *   - **StudyGroupDo** holds identity + shared ONLY. Personal fields
 *     never reach the SG side — projection at the write boundary is the
 *     load-bearing security invariant. Even if a malicious client tries
 *     to stuff `starred: true` into a shared push payload, this filter
 *     drops it before storage.
 *
 * Both directions of the propagation rely on the scope map being
 * complete and accurate. The factory's runtime invariant (added
 * alongside `mirror: 'split'`) refuses to build an entity whose
 * `fieldScopes` doesn't cover every key on its Zod schema.
 *
 * Pure data primitive. No I/O, no DO-specific types, no React. Mirrors
 * the discipline of `field-scope-router.ts`.
 */

import type { FieldScope } from '../schema/field-scopes';

/** Authority that hosts canonical state — same union as on `SyncEntity.authority`. */
export type ProjectionTarget = 'user' | 'group';

/**
 * Produce the per-authority storage view of an entity row.
 *
 *   - `target = 'user'`: returns the union (identity + shared + personal).
 *     Effectively a passthrough; identity-typed because the storage layer
 *     expects `Partial<T>` projections.
 *
 *   - `target = 'group'`: returns identity + shared only. Personal-scope
 *     keys present in `row` are dropped; unknown-scope keys are dropped
 *     too (server-receive lenience — mirrors `mergeByScope`'s behavior).
 *
 * Identity-scope keys (`id`, `deckPaths`, `linkedGroups`, …) are
 * preserved in both projections — they address the row.
 *
 * Returns a freshly-allocated object; never mutates `row`.
 */
export function projectEntity<T extends Record<string, unknown>>(
  row: Partial<T>,
  target: ProjectionTarget,
  fieldScopes: Readonly<Record<string, FieldScope>>,
): Partial<T> {
  const out: Partial<T> = {};
  for (const rawKey of Object.keys(row)) {
    const key = rawKey as keyof T;
    const scope = fieldScopes[rawKey];
    if (scope === undefined) {
      // Unknown key. Drop it on the projection — the projection is a
      // boundary, and an unclassified key has no safe destination.
      // (The partition router in `field-scope-router.ts` throws on
      // unknown keys on the WRITE side; the projection is on the
      // STORAGE side where receive-lenience matters.)
      continue;
    }
    if (scope === 'identity') {
      out[key] = row[key];
      continue;
    }
    if (target === 'user') {
      // UserDo holds everything (shared + personal).
      out[key] = row[key];
      continue;
    }
    // target === 'group' — drop personal-scope keys; keep shared.
    if (scope === 'shared') {
      out[key] = row[key];
    }
  }
  return out;
}

/**
 * Convenience: assert that a scope map covers every key on a row.
 *
 * The factory uses the same predicate (against the Zod schema's known
 * shape) at construction time. Exported here so consumers writing
 * ad-hoc projections — e.g. tests that build synthetic rows — can
 * verify their fixtures without re-implementing the loop.
 *
 * Returns the list of keys missing from `fieldScopes`. Empty array
 * means the map fully covers `row`'s keys.
 */
export function findUnscopedKeys<T extends Record<string, unknown>>(
  row: Partial<T>,
  fieldScopes: Readonly<Record<string, FieldScope>>,
): string[] {
  const missing: string[] = [];
  for (const key of Object.keys(row)) {
    if (fieldScopes[key] === undefined) {
      missing.push(key);
    }
  }
  return missing;
}
